/*
  Widerrufsseite (sections/tronso-widerruf.liquid).

  Beim Klick auf „Widerruf bestätigen“ gehen die Angaben zuerst an das
  Google-Skript (werkzeuge/widerruf/widerruf-bestaetigung.gs). Es verschickt
  die Eingangsbestätigung nach § 356a Abs. 4 BGB und antwortet mit Datum und
  Uhrzeit des Eingangs. Danach wird das Formular ganz normal als
  Shopify-Kontaktformular abgeschickt, mit dem Ergebnis im Feld
  „Eingangsbestaetigung“. So erfährt der Inhaber auch dann von dem Widerruf,
  wenn das Skript nicht erreichbar war.

  Auf der Seite nach dem Absenden zeigt das Skript Eingang und Zieladresse an,
  die es sich dafür kurz im sessionStorage merkt.
*/
(() => {
  const STORAGE_KEY = 'tronso-widerruf';
  // Großzügig: Ein kalt gestartetes Google-Skript mit Tabelle und Mailversand
  // braucht mitunter deutlich länger als die üblichen zwei, drei Sekunden.
  const TIMEOUT_MS = 30000;

  const fieldValue = (form, name) => {
    const field = form.elements.namedItem(name);
    return field && 'value' in field ? field.value.trim() : '';
  };

  async function requestConfirmation(endpoint, data) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      // Ohne eigene Header bleibt es eine einfache Anfrage ohne Preflight,
      // die Google-Web-App antwortet nach einer Weiterleitung mit CORS-Freigabe.
      const response = await fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(data),
        signal: controller.signal,
      });
      if (!response.ok) return { ok: false, fehler: 'antwort' };
      const result = await response.json();
      return result && typeof result === 'object' ? result : { ok: false, fehler: 'antwort' };
    } catch (error) {
      return { ok: false, fehler: 'netz' };
    } finally {
      clearTimeout(timer);
    }
  }

  function remember(value) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (error) {
      // Ohne Speicher bleibt nur die Detailzeile auf der Erfolgsseite weg.
    }
  }

  // Warum die Bestätigung nicht automatisch rausging, steht in der Mail an den Inhaber.
  const GRUND = {
    angaben: 'Angaben nicht erkannt, z. B. keine gültige Bestellnummer',
    limit: 'Tageslimit des Skripts erreicht',
    abgelehnt: 'vom Spamschutz abgelehnt',
  };

  // Ohne Antwort ist unklar, ob das Skript gelaufen ist: Die Anfrage kann
  // angekommen sein, nur die Antwort fehlt (Zeitüberschreitung oder ein
  // Browser-Blocker vor script.googleusercontent.com). Dann nichts behaupten.
  const UNKLAR =
    'Status UNKLAR: keine Antwort vom Google-Skript. Bitte in der Protokoll-Tabelle nachsehen. ' +
    'Steht der Widerruf dort mit „ja“, ist die Bestätigung raus. Sonst unverzüglich von Hand bestätigen, mit Datum und Uhrzeit dieser Mail.';

  async function handleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-tronso-widerruf]')) return;

    const endpoint = form.dataset.endpoint;
    if (!endpoint) return;

    // Zweiter Durchlauf über requestSubmit(): jetzt darf Shopify übernehmen,
    // samt seiner Captcha-Prüfung.
    if (form.dataset.tronsoDone === 'true') return;

    // Läuft in der Capture-Phase, also vor Shopifys eigenem Submit-Handler am
    // Formular. Der wird erst geladen, sobald jemand ein Feld anfasst, und
    // schickt sonst selbst ab, ohne dass dieser Code zum Zug kommt.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.tronsoBusy === 'true') return;
    form.dataset.tronsoBusy = 'true';

    const button = form.querySelector('button[type="submit"]');
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      if (button.dataset.labelBusy) button.textContent = button.dataset.labelBusy;
    }

    const data = {
      name: fieldValue(form, 'contact[name]'),
      email: fieldValue(form, 'contact[email]'),
      bestellnummer: fieldValue(form, 'contact[Bestellnummer]'),
      umfang: fieldValue(form, 'contact[Umfang]'),
      website: fieldValue(form, 'website'),
    };

    const result = await requestConfirmation(endpoint, data);

    const status = form.elements.namedItem('contact[Eingangsbestaetigung]');
    if (status) {
      const grund = GRUND[result.fehler];
      if (result.ok) {
        status.value = `Automatisch an ${data.email} versendet. Eingang ${result.eingang}, Vorgang ${result.vorgang}.`;
      } else if (grund) {
        status.value = `NICHT automatisch versendet (${grund}). Bitte unverzüglich von Hand bestätigen, mit Datum und Uhrzeit dieser Mail.`;
      } else {
        status.value = UNKLAR;
      }
    }

    remember({ ok: Boolean(result.ok), eingang: result.ok ? result.eingang : '', email: data.email });

    form.dataset.tronsoDone = 'true';
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(event.submitter || undefined);
    } else {
      form.submit();
    }
  }

  function showResult() {
    const success = document.querySelector('[data-tronso-widerruf-erfolg]');
    if (!success) return;

    let stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      stored = null;
    }
    if (!stored) return;

    if (stored.ok) {
      const line = success.querySelector('[data-tronso-widerruf-eingang]');
      if (line && stored.eingang) {
        line.textContent = `Eingang: ${stored.eingang}. Bestätigung an ${stored.email}.`;
        line.hidden = false;
      }
    } else {
      const ok = success.querySelector('[data-tronso-widerruf-ok]');
      const failure = success.querySelector('[data-tronso-widerruf-fehler]');
      if (ok && failure) {
        ok.hidden = true;
        failure.hidden = false;
      }
    }
  }

  document.addEventListener('submit', handleSubmit, true);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showResult);
  } else {
    showResult();
  }
})();
